import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    Divider,
    Typography,
    Collapse
} from '@mui/material';
import {
    ExpandLess,
    ExpandMore
} from '@mui/icons-material';

import { MENU_ITEMS, MenuItem } from '../constants/menu';
import { usePermission } from '../contexts/PermissionContext';

interface NavigationProps {
    open: boolean;
    onClose: () => void;
    mode: 'side' | 'over';
}

interface NavGroup {
    label: string;
    items: MenuItem[];
}

const Navigation: React.FC<NavigationProps> = ({ open, onClose, mode }) => {
    const location = useLocation();
    const { hasPermission, isSuperAdmin, currentAdmin } = usePermission();
    const [navigationGroups, setNavigationGroups] = useState<NavGroup[]>([]);
    const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        const filteredGroups = MENU_ITEMS.map(group => {
            const filteredItems = group.items.filter(item => {
                if (isSuperAdmin()) return true;
                if (!item.permission) return true;

                const menuPermissions = currentAdmin?.menu_permission || (currentAdmin as any)?.menuPermissions || (currentAdmin as any)?.menuPermission || [];
                if (menuPermissions.includes('*')) return true;

                return hasPermission(item.permission);
            });

            return {
                ...group,
                items: filteredItems
            };
        }).filter(group => group.items.length > 0);

        setNavigationGroups(filteredGroups);

        // Auto-expand the group that has the active item
        const initialOpenGroups: { [key: string]: boolean } = {};
        filteredGroups.forEach(group => {
            const hasActiveItem = group.items.some(item => location.pathname === item.link);
            if (hasActiveItem || group.label === 'Dashboard' || group.label === 'Orders') {
                initialOpenGroups[group.label] = true;
            }
        });
        setOpenGroups(prev => ({ ...prev, ...initialOpenGroups }));
    }, [hasPermission, isSuperAdmin, currentAdmin, location.pathname]);

    const toggleGroup = (label: string) => {
        setOpenGroups(prev => ({
            ...prev,
            [label]: !prev[label]
        }));
    };

    const drawerContent = (
        <div className="bg-gray-800 w-full min-h-screen h-full flex flex-col">
            {/* Logo */}
            <div className="flex items-center justify-center h-20 p-6 pb-0 mb-[20px] flex-shrink-0">
                <img
                    className="w-30 md:w-40"
                    src="/assets/images/logo/KOKI-logo.svg"
                    alt="Logo image"
                />
            </div>

            {/* Navigation List - Scrollable */}
            <div className="flex-1 overflow-y-auto py-2 px-[12px] custom-scrollbar pb-10">
                {navigationGroups.map((group, groupIdx) => {
                    const isOpen = openGroups[group.label];
                    const hasActiveItemInGroup = group.items.some(item => location.pathname === item.link);

                    return (
                        <Box key={group.label} sx={{ mb: 1 }}>
                            <ListItemButton
                                onClick={() => toggleGroup(group.label)}
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                color: hasActiveItemInGroup ? '#38BDF8' : 'rgba(255, 255, 255, 0.7)',
                                                fontWeight: 800,
                                                fontSize: '0.75rem',
                                                letterSpacing: '0.05em',
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {group.label}
                                        </Typography>
                                    }
                                />
                                {isOpen ? (
                                    <ExpandLess sx={{ color: hasActiveItemInGroup ? '#38BDF8' : 'rgba(255, 255, 255, 0.5)', fontSize: '1.2rem' }} />
                                ) : (
                                    <ExpandMore sx={{ color: hasActiveItemInGroup ? '#38BDF8' : 'rgba(255, 255, 255, 0.5)', fontSize: '1.2rem' }} />
                                )}
                            </ListItemButton>

                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <List className="py-0 mt-1 pl-2">
                                    {group.items.map((item) => (
                                        <ListItem key={item.id} disablePadding className='mb-1'>
                                            <ListItemButton
                                                component={NavLink}
                                                to={item.link}
                                                className={`p-[8px] md:py-2.5 md:px-4 transition-all rounded-[6px] text-white ${location.pathname === item.link ? 'bg-[#0EA5E9] text-white shadow-lg' : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'}`}
                                                onClick={mode === 'over' ? onClose : undefined}
                                                sx={{
                                                    '&.active': {
                                                        backgroundColor: '#0EA5E9',
                                                        '& .MuiListItemIcon-root': {
                                                            color: 'white'
                                                        }
                                                    }
                                                }}
                                            >
                                                <ListItemIcon className={`min-w-[32px] ${location.pathname === item.link ? 'text-white' : 'text-[#94A3B8]'}`}>
                                                    <div className="text-xl flex items-center justify-center">{item.icon}</div>
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.title}
                                                    primaryTypographyProps={{
                                                        style: {
                                                            fontSize: '14px',
                                                            fontWeight: location.pathname === item.link ? 700 : 500,
                                                            color: location.pathname === item.link ? '#ffffff' : 'inherit'
                                                        }
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                            {groupIdx < navigationGroups.length - 1 && !isOpen && (
                                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)', mt: 1, mb: 1, mx: 2 }} />
                            )}
                        </Box>
                    );
                })}
            </div>
        </div>
    );

    if (mode === 'over') {
        return (
            <Drawer
                anchor="left"
                open={open}
                onClose={onClose}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <Box sx={{ width: 280 }}>
                    {drawerContent}
                </Box>
            </Drawer>
        );
    }

    return (
        <div className="h-full w-full border-r border-gray-700">
            {drawerContent}
        </div>
    );
};

export default Navigation;
